import { baseApi } from "@/app/store/api/baseApi";

import type { Paginated } from "@/shared/api/types";

import type {
  AddDonorPayload,
  DonationHistoryRow,
  DonorDonationType,
  DonorKind,
  DonorPledge,
  DonorRecency,
  OrgPledge,
  DonorRow,
  RecentActivityType,
  RecentGift,
  ThankYouQueueGift,
} from "../model/types";

function markGiftThankedInCache(
  dispatch: (action: never) => void,
  getState: () => unknown,
  giftId: number,
) {
  const recentArgs = donorsApi.util.selectCachedArgsForQuery(
    getState() as never,
    "listRecentGifts",
  );
  for (const arg of recentArgs) {
    dispatch(
      donorsApi.util.updateQueryData("listRecentGifts", arg, (draft) => {
        for (const row of draft.results ?? []) {
          if (row.id === giftId || row.thank_you_queue_id === giftId) {
            row.thanked = true;
          }
        }
      }) as never,
    );
  }
  const historyArgs = donorsApi.util.selectCachedArgsForQuery(
    getState() as never,
    "listDonationHistory",
  );
  for (const arg of historyArgs) {
    dispatch(
      donorsApi.util.updateQueryData("listDonationHistory", arg, (draft) => {
        for (const row of draft.results ?? []) {
          if (row.gift_id === giftId || (row.kind === "gift" && row.id === giftId)) {
            row.thanked = true;
          }
        }
      }) as never,
    );
  }
}

export const donorsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listDonors: build.query<
      Paginated<DonorRow>,
      {
        kind?: DonorKind;
        search?: string;
        last_gift?: DonorRecency;
        open_promise?: boolean;
        donation_type?: DonorDonationType;
        page?: number;
        page_size?: number;
      }
    >({
      query: ({ kind, search, last_gift, open_promise, donation_type, page, page_size }) => ({
        url: "/people/donors/",
        params: {
          ...(kind ? { kind } : {}),
          ...(search ? { search } : {}),
          ...(last_gift ? { last_gift } : {}),
          ...(open_promise ? { open_promise: "1" } : {}),
          ...(donation_type ? { donation_type } : {}),
          ...(page ? { page } : {}),
          ...(page_size ? { page_size } : {}),
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id, kind }) => ({
                type: "Donor" as const,
                id: `${kind}-${id}`,
              })),
              { type: "Donor", id: "LIST" },
            ]
          : [{ type: "Donor", id: "LIST" }],
    }),
    createDonor: build.mutation<DonorRow, AddDonorPayload>({
      query: (body) => ({ url: "/people/donors/", method: "POST", body }),
      invalidatesTags: [
        { type: "Donor", id: "LIST" },
        { type: "Person", id: "LIST" },
        { type: "Organization", id: "LIST" },
      ],
    }),
    listRecentGifts: build.query<
      Paginated<RecentGift>,
      {
        page?: number;
        page_size?: number;
        search?: string;
        received_after?: string;
        received_before?: string;
        fund?: string;
        activity_type?: RecentActivityType;
      } | void
    >({
      query: (params) => ({
        url: "/people/donors/recent-gifts/",
        params: params
          ? {
              ...(params.page ? { page: params.page } : {}),
              ...(params.page_size ? { page_size: params.page_size } : {}),
              ...(params.search ? { search: params.search } : {}),
              ...(params.received_after ? { received_after: params.received_after } : {}),
              ...(params.received_before ? { received_before: params.received_before } : {}),
              ...(params.fund ? { fund: params.fund } : {}),
              ...(params.activity_type ? { activity_type: params.activity_type } : {}),
            }
          : undefined,
      }),
      providesTags: [{ type: "Donor", id: "RECENT" }],
    }),
    listOrgPledges: build.query<
      Paginated<OrgPledge>,
      {
        page?: number;
        page_size?: number;
        search?: string;
        pledged_after?: string;
        pledged_before?: string;
      } | void
    >({
      query: (params) => ({
        url: "/people/donors/pledges/",
        params: params
          ? {
              ...(params.page ? { page: params.page } : {}),
              ...(params.page_size ? { page_size: params.page_size } : {}),
              ...(params.search ? { search: params.search } : {}),
              ...(params.pledged_after ? { pledged_after: params.pledged_after } : {}),
              ...(params.pledged_before ? { pledged_before: params.pledged_before } : {}),
            }
          : undefined,
      }),
      providesTags: [{ type: "Donor", id: "PLEDGES" }],
    }),
    listDonorPledges: build.query<
      Paginated<DonorPledge>,
      { kind: DonorKind; id: number; page?: number; page_size?: number }
    >({
      query: ({ kind, id, page, page_size }) => ({
        url:
          kind === "organization"
            ? `/people/organizations/${id}/pledges/`
            : `/people/persons/${id}/pledges/`,
        params: {
          ...(page ? { page } : {}),
          ...(page_size ? { page_size } : {}),
        },
      }),
      providesTags: (_result, _err, { kind, id }) => [
        { type: "Donor" as const, id: `pledges-${kind}-${id}` },
      ],
    }),
    listDonationHistory: build.query<
      { results: DonationHistoryRow[] },
      { kind: DonorKind; id: number }
    >({
      query: ({ kind, id }) => ({
        url:
          kind === "organization"
            ? `/people/organizations/${id}/donation-history/`
            : `/people/persons/${id}/donation-history/`,
      }),
      providesTags: (_result, _err, { kind, id }) => [
        { type: "Donor" as const, id: `history-${kind}-${id}` },
      ],
    }),
    listThankYouQueue: build.query<
      Paginated<ThankYouQueueGift>,
      { page?: number; page_size?: number }
    >({
      query: ({ page, page_size } = {}) => ({
        url: "/people/donors/thank-you-queue/",
        params: {
          ...(page ? { page } : {}),
          ...(page_size ? { page_size } : {}),
        },
      }),
      providesTags: [{ type: "Donor", id: "THANK_QUEUE" }],
    }),
    sendGiftThankYou: build.mutation<ThankYouQueueGift, number>({
      query: (giftId) => ({
        url: `/people/donors/thank-you-queue/${giftId}/send-thank-you/`,
        method: "POST",
      }),
      async onQueryStarted(giftId, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        markGiftThankedInCache(dispatch, getState, giftId);
      },
      invalidatesTags: [
        { type: "Donor", id: "THANK_QUEUE" },
        { type: "Donor", id: "LIST" },
        { type: "Contribution", id: "LIST" },
      ],
    }),
    markGiftThanked: build.mutation<ThankYouQueueGift, number>({
      query: (giftId) => ({
        url: `/people/donors/thank-you-queue/${giftId}/mark-thanked/`,
        method: "POST",
      }),
      async onQueryStarted(giftId, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        markGiftThankedInCache(dispatch, getState, giftId);
      },
      invalidatesTags: [
        { type: "Donor", id: "THANK_QUEUE" },
        { type: "Donor", id: "LIST" },
        { type: "Contribution", id: "LIST" },
      ],
    }),
    recordPledgeReceived: build.mutation<
      DonationHistoryRow,
      { kind: DonorKind; id: number; pledgeId: number }
    >({
      query: ({ kind, id, pledgeId }) => ({
        url:
          kind === "organization"
            ? `/people/organizations/${id}/pledges/${pledgeId}/record-received/`
            : `/people/persons/${id}/pledges/${pledgeId}/record-received/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _err, { kind, id }) => [
        { type: "Donor", id: `history-${kind}-${id}` },
        { type: "Donor", id: `pledges-${kind}-${id}` },
        { type: "Donor", id: "LIST" },
        { type: "Donor", id: "PLEDGES" },
        { type: "Contribution", id: "LIST" },
        {
          type: kind === "organization" ? "Organization" : "Person",
          id: `DONOR-${id}`,
        },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListDonorsQuery,
  useCreateDonorMutation,
  useListRecentGiftsQuery,
  useListOrgPledgesQuery,
  useListDonorPledgesQuery,
  useListDonationHistoryQuery,
  useListThankYouQueueQuery,
  useSendGiftThankYouMutation,
  useMarkGiftThankedMutation,
  useRecordPledgeReceivedMutation,
} = donorsApi;
